import { ChevronDown } from "lucide-react";
import { cn } from "@ui";
import { ProfileActionsDropdown } from "./profile-actions-dropdown";
import { useContactStatus } from "./contact-status-context";

export { ProfileActionsDropdown } from "./profile-actions-dropdown";

export type InsitiProfileType = "candidat" | "enseigne" | "ville" | "location";

export interface InsitiProfile {
  type: InsitiProfileType;
  id: number;
  name: string;
  user_id?: number;
}

/**
 * Parse insiti:// URLs
 * Format: insiti://type/id or insiti://type/id?user_id=XXX
 */
export function parseInsitiUrl(
  href: string | undefined,
): Omit<InsitiProfile, "name"> | null {
  if (!href) return null;

  // Match: insiti://type/id with optional ?user_id=XXX
  const match = href.match(/^insiti:\/\/(\w+)\/(\d+)(\?user_id=(\d+))?$/);
  if (!match) return null;

  const type = match[1] as InsitiProfileType;
  const validTypes: InsitiProfileType[] = [
    "candidat",
    "enseigne",
    "ville",
    "location",
  ];
  if (!validTypes.includes(type)) return null;

  const id = parseInt(match[2], 10);
  const user_id = match[4] ? parseInt(match[4], 10) : undefined;

  return { type, id, user_id };
}

interface InsitiLinkProps {
  href?: string;
  children?: React.ReactNode;
}

export function InsitiLink({ href, children }: InsitiLinkProps) {
  const parsed = parseInsitiUrl(href);
  // Called unconditionally (rules of hooks); returns undefined without a
  // user_id or when no ContactStatusProvider is mounted.
  const status = useContactStatus(parsed?.user_id);

  // Regular external link - render normally
  if (!parsed) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-default-600 hover:text-default-800 underline"
      >
        {children}
      </a>
    );
  }

  // The policy's display_name is the obfuscation source of truth; fall back to
  // the link text until the status loads (or when there is no provider).
  const childText =
    typeof children === "string"
      ? children
      : Array.isArray(children)
        ? children.join("")
        : String(children ?? "");
  const name = status?.display_name ?? childText;
  // Greyed (slate) until the profile is in your contacts; purple on hover.
  // Once revealed/in contacts, the pill stays purple (unchanged).
  const greyed = Boolean(status && !status.revealed);

  const profile: InsitiProfile = {
    ...parsed,
    name,
  };

  return (
    <span className="inline-block">
      <ProfileActionsDropdown
        profile={profile}
        trigger={
          <button
            type="button"
            className={cn(
              "group px-2 py-0.5 rounded-lg ring-1 inline-flex items-center gap-0.5 font-medium transition-colors min-w-0",
              greyed
                ? "bg-slate-50 ring-slate-200 hover:bg-default-50 hover:ring-default-200"
                : "bg-default-50 ring-default-200",
            )}
          >
            <span
              className={cn(
                "truncate",
                greyed
                  ? "text-slate-500 group-hover:text-default-600"
                  : "text-default-500",
              )}
            >
              {name}
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </button>
        }
        placement="bottom-start"
        inline
      />
    </span>
  );
}
